import ResponsiveSidebar from "../../General/ResponsiveSidebar/ResponsiveSidebar";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  EventNote as EventNoteIcon,
  EventSeat as EventSeatIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  LocalShipping as LocalShippingIcon,
  MyLocation as MyLocationIcon,
  Person as PersonIcon,
  Lock as LockIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import useAuth from "../../../hooks/useAuth";

const drawerWidth = 240;
const collapsedWidth = 60;

interface SidebarProps {
  open?: boolean;
  toggle?: () => void;
}

type MenuItem = {
  text: string;
  icon: JSX.Element;
  path: string;
};

const OperationsSidebar = ({
  open = false,
  toggle = () => {},
}: SidebarProps) => {
  const location = useLocation();
  const { role } = useAuth();

  const menuItems = [
    { text: "Planner", icon: <EventNoteIcon />, path: "/operations/planner" },
    role?.some((r) =>
      ["Operations", "OperationsManager", "OperationsAdmin"].includes(r),
    ) && {
      text: "Passwords",
      icon: <LockIcon />,
      path: "/operations/dispatchers-passwords",
    },
    {
      text: "Broker Blacklist",
      icon: <AssessmentIcon />,
      path: "/operations/broker-blacklist",
    },
    role?.includes("OperationsManager") && {
      text: "Drivers",
      icon: <PersonIcon />,
      path: "/operations/drivers",
    },
    role?.includes("OperationsManager") && {
      text: "Driver performance",
      icon: <AssessmentIcon />,
      path: "/operations/driver-performance",
    },
    {
      text: "Find my truck",
      icon: <MyLocationIcon />,
      path: "/operations/find-my-truck",
    },
    {
      text: "Available trucks",
      icon: <LocalShippingIcon />,
      path: "/operations/available-trucks",
    },
    role?.includes("OperationsManager") && {
      text: "Seated report",
      icon: <EventSeatIcon />,
      path: "/operations/seated-report",
    },
    role?.includes("OperationsManager") && {
      text: "Utilization report",
      icon: <BarChartIcon />,
      path: "/operations/utilization-report",
    },
    role?.includes("OperationsAdmin") && {
      text: "Gross report",
      icon: <AssessmentIcon />,
      path: "/operations/gross-report",
    },
  ].filter(Boolean) as MenuItem[];

  const isSelected = (path: string) =>
    location.pathname.startsWith(path) ||
    (path === "/operations/planner" && location.pathname === "/operations");

  return (
    <ResponsiveSidebar
      open={open}
      onClose={toggle}
      sx={(theme) => ({
        width: open ? drawerWidth : collapsedWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidth : collapsedWidth,
          boxSizing: "border-box",
          top: 64,
          height: "calc(100% - 64px)",
          overflowX: "hidden",
          position: "fixed",
          transition: theme.transitions.create("width", {
            duration: theme.transitions.duration.standard,
          }),
        },
      })}
    >
      <Toolbar sx={{ justifyContent: open ? "flex-end" : "center" }}>
        <IconButton
          onClick={toggle}
          aria-label={
            open ? "Close section navigation" : "Open section navigation"
          }
        >
          {open ? <ChevronLeftIcon /> : <MenuIcon />}
        </IconButton>
      </Toolbar>
      <List>
        {menuItems.map((item) => {
          const button = (
            <ListItemButton
              key={item.text}
              selected={isSelected(item.path)}
              component={RouterLink}
              to={item.path}
              sx={{ justifyContent: open ? "initial" : "center" }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 0,
                  justifyContent: "center",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{ opacity: open ? 1 : 0 }}
              />
            </ListItemButton>
          );

          return open ? (
            button
          ) : (
            <Tooltip key={item.text} title={item.text} placement="right">
              {button}
            </Tooltip>
          );
        })}
      </List>
    </ResponsiveSidebar>
  );
};

export default OperationsSidebar;
